import React from 'react'
import FolderRow, { EmptyFolderRow } from 'components/sidebar/components/folders/FolderRow'
import { RenameResult } from 'components/sidebar/components/folders/rowParts'
import ResourceRow, { resourcePath } from 'components/sidebar/components/folders/ResourceRow'
import { SelectionModifiers } from 'components/sidebar/components/folders/selection'
import { DropZone, SidebarFolderNode, SidebarNode } from 'components/sidebar/components/folders/tree'
import { RowDropIndicator } from 'components/sidebar/components/folders/dnd'
import { SelectionJoin } from 'components/sidebar/surface'

/**
 * What the tree hands its rows. Rows get primitives and these id-taking callbacks rather than
 * reading hooks themselves, so each row's props only change when that row does. The rows are
 * memoised, so the callbacks must keep their identity across renders (useStableHandler); the
 * predicates are only called here, to give each row its own booleans.
 */
export interface SidebarTreeView {
  hubId: string
  brandColour?: string
  /** The document or table the page on screen shows. */
  activeId: string | null
  expanded: Set<string>
  /** Hub Owners: rename, drag folders, add subfolders. */
  canManage: boolean
  /** Rows a drag may drop into or beside: on whenever the viewer can move anything. */
  canDrop: boolean
  /** Whether the viewer may select and drag this row. */
  isMovable: (id: string) => boolean
  /** Whether F2 renames this document or table for the viewer (its Owners, one row at a time). */
  canRename: (id: string) => boolean
  /** The folder, document or table showing its rename box, if any. */
  renamingId: string | null
  isSelected: (id: string) => boolean
  /** Some rows are selected. */
  hasSelection: boolean
  /** Which of a selected row's neighbours on screen are selected too ('none' for a row not selected). */
  selectionJoin: (id: string) => SelectionJoin
  /** What a folder's Delete & archive would take away, while it's pointed at or confirmed. */
  isPreviewed: (id: string) => boolean
  /** Where the drag in progress would drop, if anywhere. */
  dropIndicator: RowDropIndicator | null
  /** Part of the drag in progress. */
  isDragged: (id: string) => boolean
  toggle: (folderId: string) => void
  /** A row click, offered to the selection first; true when the selection claimed it. */
  selectClick: (event: SelectionModifiers, id: string) => boolean
  startRename: (id: string) => void
  /** The rename box closed, and how. */
  endRename: (id: string, result: RenameResult) => void
  /**
   * Opens a row's menu on a right-click, unless a drag is on (`dragging`). A row it leaves alone
   * keeps the browser's menu.
   */
  onContextMenu?: (event: React.MouseEvent<HTMLElement>, id: string, dragging: boolean) => void
  /** Opens a folder's create menu from the + on its row. Unset for viewers who can't create anything. */
  newIn?: (folderId: string, button: HTMLElement) => void
  /** Opens a row's menu from its ⋯, for the rows that have one. */
  openMenu: (id: string, button: HTMLElement) => void
  /** Whether a row has a menu for this viewer, and so a ⋯. */
  hasMenu: (id: string) => boolean
}

/** The drop indicator for one row, as a primitive, so only the rows it moves between change. */
const indicatorFor = (view: SidebarTreeView, id: string): DropZone | null =>
  view.dropIndicator?.rowId === id ? view.dropIndicator.kind : null

interface FolderBranchProps {
  folder: SidebarFolderNode
  view: SidebarTreeView
}

/** An open folder's contents, or the muted Empty row when it has none. */
const FolderChildren: React.FC<FolderBranchProps> = ({ folder, view }) => {
  if (folder.children.length === 0) {
    return <EmptyFolderRow folderId={folder.id} depth={folder.depth + 1} droppable={view.canDrop} />
  }
  return <SidebarTree nodes={folder.children} view={view} />
}

/** A folder row, and beneath it its children while it's open. Collapsed children aren't rendered. */
const FolderBranch: React.FC<FolderBranchProps> = ({ folder, view }) => {
  const open = view.expanded.has(folder.id)
  return (
    <div>
      <FolderRow
        id={folder.id}
        name={folder.name}
        depth={folder.depth}
        expanded={open}
        canManage={view.canManage}
        droppable={view.canDrop}
        renaming={view.renamingId === folder.id}
        selected={view.isSelected(folder.id)}
        selectionJoin={view.selectionJoin(folder.id)}
        previewed={view.isPreviewed(folder.id)}
        dropIndicator={indicatorFor(view, folder.id)}
        dragging={view.isDragged(folder.id)}
        brandColour={view.brandColour}
        onToggle={view.toggle}
        onRenameStart={view.startRename}
        onRenameEnd={view.endRename}
        onSelectClick={view.selectClick}
        onContextMenu={view.onContextMenu}
        onNewIn={view.newIn}
        onOpenMenu={view.hasMenu(folder.id) ? view.openMenu : undefined}
      />
      {open && <FolderChildren folder={folder} view={view} />}
    </div>
  )
}

interface SidebarTreeProps {
  nodes: SidebarNode[]
  view: SidebarTreeView
}

/** One level of the sidebar tree, in the order buildSidebarTree gave it. */
const SidebarTree: React.FC<SidebarTreeProps> = ({ nodes, view }) => (
  <>
    {nodes.map((node) =>
      node.kind === 'folder' ? (
        <FolderBranch key={node.id} folder={node} view={view} />
      ) : (
        <ResourceRow
          key={node.id}
          id={node.id}
          kind={node.kind}
          name={node.name}
          href={resourcePath(view.hubId, node.resource)}
          depth={node.depth}
          active={view.activeId === node.id}
          selected={view.isSelected(node.id)}
          selectionJoin={view.selectionJoin(node.id)}
          previewed={view.isPreviewed(node.id)}
          // Only the active row reads it; for the rest it stays false, so a selection starting or
          // clearing doesn't re-render every row.
          outsideSelection={view.activeId === node.id && view.hasSelection && !view.isSelected(node.id)}
          canMove={view.isMovable(node.id)}
          droppable={view.canDrop}
          canRename={view.canRename(node.id)}
          renaming={view.renamingId === node.id}
          dropIndicator={indicatorFor(view, node.id)}
          dragging={view.isDragged(node.id)}
          brandColour={view.brandColour}
          onSelectClick={view.selectClick}
          onRenameStart={view.startRename}
          onRenameEnd={view.endRename}
          onContextMenu={view.onContextMenu}
          onOpenMenu={view.hasMenu(node.id) ? view.openMenu : undefined}
        />
      )
    )}
  </>
)

export default SidebarTree
